import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ROUTE} from 'core/routes';
import {isAdmin$, isDirector$} from 'models/auth';
import {Gate, pageNumberChanged, staffState$} from 'models/profile/staff/table';
import {StaffListFormattedRow, StaffListRawRow} from 'types/profile';

interface Props {
    configFunc: (t: TFunction, isDirectorOrAdmin?: boolean) => Array<any>;
    dataMapper: (
        data: StaffListRawRow[] | undefined,
        t: TFunction,
        updateUser: () => void,
    ) => StaffListFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const StaffGridTable = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const {t} = useTranslation();
    const state = useStore(staffState$);
    const history = useHistory();
    const isDirector = useStore(isDirector$);
    const isAdmin = useStore(isAdmin$);

    const isDirectorOrAdmin = useMemo(() => isDirector || isAdmin, [isDirector, isAdmin]);
    useGate(Gate);
    const columns = useMemo(() => mapTableColumns(configFunc(t, isDirectorOrAdmin)), [
        t,
        isDirectorOrAdmin,
    ]);
    const mappedData = React.useMemo(
        () => dataMapper(state.data?.content, t, () => history.push(ROUTE.STAFF_UPDATE)),
        [dataMapper, state, t],
    );

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={state.data?.totalElements}
            pageCount={state.data?.totalPages}
            pageIndex={state.data?.pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={state.isLoading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'staffGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            exportConfig={exportConfig}
        />
    );
};
